import app from 'flarum/forum/app';
// v0.18.100 — 候选项的头像改用 **Flarum 原生 <Avatar>**。
//   来源: 辉哥 10/26 原话「**搜索下拉框, 里面的用户头像要用『消课统计』tab 中,
//   经办人列表中数据那种用户头像展示方式**」
//   ⇒ 基准就是 js/src/forum/components/StatsTab.js:186-191 的 rankingRow 里的
//     `<Avatar user={userModel} />` —— 本组件用的是**同一个** core 组件、同一种表现。
import Avatar from 'flarum/common/components/Avatar';
// ⚠️ 必须走**子路径**拿 default export —— core 的 common.ts 里那行
//   `import './utils/stringToColor'` 是 side-effect import, **不 re-export**,
//   从 'flarum/common' 拿不到具名导出 (与 CalendarTab.js:8 同一个坑, 那儿有完整注释)。
import stringToColor from 'flarum/common/utils/stringToColor';
import Api from './Api';
// v0.19.14 补记 — 诊断开关的**同一份判定** (`?zskbdiag=1`)
//   ⇒ `window.__zsModalTopPin` 只在**诊断开着**时才暴露 `disable()` / `enable()`
//     (验收方 L1: 生产环境控制台不该能关掉补偿)
//   ⚠️ `iosKeyboardDiag.js` **不 import 任何东西** ⇒ 这里不会形成循环依赖。
import { isIosKeyboardDiagEnabled } from './iosKeyboardDiag';

/**
 * v0.16.7 — 「可搜索用户单选」通用组件 (辉哥 10/11 拍板: 把经办人那套搜索下拉做成通用方法)
 *
 * ## 为什么抽
 *   客户跟进 modal 的「经办人」是本扩展第一个**带搜索**的用户下拉, v0.16.6 又在它上面
 *   修了一轮很细的交互回归 (浮动定位 / 三条关闭时机 / 候选项能点中)。同一个 modal 里
 *   新增「教练」时如果再抄一遍, 下一轮回归就得**两个下拉各测一遍**, 漏一边就是线上 bug。
 *   所以本轮把它整体抽出来, 一处实现, 两个 (以及以后 N 个) 下拉共用。
 *
 * ## 一个实例 = 一个 modal 里的 N 个下拉
 *   本组件**不是** Mithril 组件 (没有 Component/oninit), 而是「持有状态 + 产出 JSX」的一坨:
 *   Modal 在 oninit 里 new 一个, 模板里 `picker.render('coach_id')` 嵌进去。
 *   这样状态仍然活在宿主 Modal 上 (m.redraw() 才管用), 又能一处实现 N 个字段。
 *   每个下拉的 query / open / 候选列表**按 key 独立**, 所以两个下拉互不干扰。
 *
 * ## DOM 契约 (改名前务必读)
 *   - `.ZS-agent-search[data-zs-picker=<key>]`  锚点, position:relative
 *   - `.ZS-agent-drop[data-zs-picker=<key>]`    浮动列表 (position:absolute, 脱离文档流)
 *   - `.ZS-agent-opt`                           候选项
 *   `data-zs-picker` 是 v0.16.7 新增的: v0.16.6 靠 `closest('.ZS-agent-search')` 判断
 *   「点在内部还是外部」, 一个下拉时够用, 两个下拉就**不够** —— 开着教练列表点经办人输入框,
 *   那个框也在某个 `.ZS-agent-search` 里, 老逻辑会误判成「内部」从而拒绝关闭。补了 key 之后
 *   才能区分「自己人」和「隔壁那个」。LESS 侧无需为此改任何选择器。
 *
 * ## ⚠️ v0.16.6 修好的回归点, 一个都不能在抽组件时丢掉
 *   ① 候选项 `onmousedown preventDefault` —— 不拦住的话 blur 先于 click, 一条都选不中;
 *   ② 三条关闭时机 (blur / 点外部 / Esc), 且 Esc 要 keydown + keyup **两个都拦**;
 *   ③ `_suppressFocusOpen` —— Flarum focus trap 会在外部点击后把焦点还给输入框,
 *      没有这个标记就会「怎么点都关不掉」(列表原样弹回来);
 *   ④ `onclick` 重开 —— ① 压掉失焦后焦点还留在输入框上, 再点不会触发 onfocus;
 *   ⑤ 向上翻转 —— 祖先 `.Modal-content` 是 Flarum core 的 overflow:hidden, 不翻会被**看不见地**裁掉。
 *
 * ## 候选来源
 *   统一走 `GET /users?role=<role>&school_id=N` (ListUsersByRoleController),
 *   候选身份由后台设置 `scheduler-role-coach-group.group` 等映射的 Flarum 用户组决定。
 *   ⚠️ RoleResolver 优先级是 **student > coach > sales**, 所以「既是学员又是教练」的人
 *     在 `role=coach` 里查不到 (会被判成 student)。本地/生产实测该重叠为 **0 人**
 *     (教练组成员都不在学员组), 所以直接用现有端点, 没有另写查询。
 *
 * ## v0.17.12 新增的 per-field 开关 (全部可选, 不传 = 改前行为逐字节不变)
 *   · `pick: () => Array`  —— 宿主**已有**数组时直接复用, 零网络请求 (约课 modal 的教练/学员)
 *   · `idOf: (item) => id`  —— 候选项主键口径 (/users 返 `id`, students-list 返 `user_id`)
 *   · `bare: true`          —— 只输出 `.ZS-agent-search`, 不套 `.ZS-form-group` + label 外壳
 *                             (宿主自己已有 label/外壳时用, 否则会出现两个标签并破坏 grid)
 *   · `clearable !== false` —— 不传就有清除按钮 (既有能力, 顺带在此列出)
 *   · `suppressAutofocusOpen: true` —— 免疫「宿主 modal 打开时那次程序化 focus」
 *                             (宿主自己的第一个 input 会被 ziven-core 全局 onready 覆盖
 *                              强制聚焦; 开着这个开关, 那一次 focus 不再展开下拉。
 *                              判据与实测数据见构造器里 v0.17.13 那段注释)
 * 宿主层另有两个可选回调:
 *   · `setValue(key, value, item)` 的第三个参数 —— 选中时给出候选项**对象**, 清除时给 null
 *   · `currentObject: (key) => item|null`     —— 候选里找不到当前值时用于**回显名字**
 *
 * ## 用法 (最小示例)
 * ```js
 * this.picker = new SearchableUserPicker({
 *   host: this,                                  // 提供 this.element (取 boundingClientRect 用)
 *   getSchoolId: () => this.activeSchoolId,
 *   getValue: (key) => this.form[key],
 *   setValue: (key, v) => { this.form[key] = v; },
 *   fields: {
 *     sales_id: { role: 'coach,sales', label: '经办人', icon: 'fa-user-tie',
 *                 required: true,  groupClass: 'ZS-fg-agent', defaultCurrentUser: true },
 *     coach_id: { role: 'coach',     label: '教练',   icon: 'fa-user-tie',
 *                 groupClass: 'ZS-fg-coach', placeholder: '输入姓名筛选…' },
 *   },
 * });
 * // oninit 之后 await this.picker.loadAll();
 * // oncreate 里 this.picker.mount(); onupdate 里 this.picker.afterUpdate(); onremove 里 destroy()
 * ```
 * 模板里: `<div className="ZS-form-row">{this.picker.render('sales_id')}{this.picker.render('coach_id')}</div>`
 */
/**
 * v0.18.38 — 候选列表**按 id 去重**
 *
 * ## 为什么必须去重 (这不是洁癖, 是 mithril 的**崩溃**)
 *   本组件 `render()` 里给每个候选项的 key 取的是 **用户的 id**:
 *     `key={this.idOfItem(key, u)}`
 *   而后端 `GET /api/ziiven/scheduler/users?role=...` 对「**同时命中多个角色组**」的人
 *   会返**重复行** —— 它是按角色组 join 出来的, 一个人命中 2 个组就有 2 行。
 *   实测 (本地 school 6):
 *     role=coach                    → 1 行, 无重复
 *     role=coach,sales              → 4 行, 无重复
 *     role=coach,sales,manager      → 6 行, **id=101 两次 + id=2 两次**
 *     role=coach,sales,manager (不限校区) → 8 行, **id=2 两次 + id=101 两次**
 *   ⇒ 同一个 key 出现两次 ⇒ **mithril 的 keyed diff 崩**:
 *     keyed diff 的核心是 `f = e[$]; e[$] = null;` —— 同一个 key 被第二次命中时,
 *     `e[$]` 已经是 null ⇒ 紧接着 `updateNodes` 里的 `var l = e.tag` 就炸:
 *       **TypeError: Cannot read properties of null (reading 'tag')**
 *     表现 = 展开下拉时**控制台报错**, 且那一帧候选项渲染不出来。
 *
 * ## 为什么在前端去重, 而不是修后端
 *   · 后端那个 join 少一个 DISTINCT 是根因, 但**后端不在本轮范围**;
 *   · 而且前端**必须**对上游的重复行免疫 —— 否则任何一次「多角色筛选」都会炸,
 *     本组件是**通用组件** (销售跟进新建 modal / 约课 modal 的体验课 tab 都在用)。
 *
 * ## 为什么保留**第一条**
 *   重复的两行是**同一个人**, 保留第一条 ⇒ 顺序不变 (后端按什么顺序返就还什么顺序),
 *   第一条自带的角色标签 (教练/店长/销售) 也保留下来。
 *   ⇒ 可见变化: 同一个人的两行变**一行**。这本身就是修正 (一个人不该在下拉里出现两次)。
 *
 * @param {Array} items 原始候选
 * @param {(item) => (number|string|null)} idOf 取主键的口径 (宿主可自定义)
 * @returns {Array} 去重后的候选 (保持原顺序)
 */
function dedupeById(items, idOf) {
  const seen = new Set();
  const out = [];
  for (const it of items || []) {
    const id = idOf(it);
    // 主键取不到 (null/undefined) 的行**不丢**, 只是没法判重 ⇒ 按"唯一"处理, 原样保留
    if (id != null) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(it);
  }
  return out;
}

/**
 * v0.19.01 — 抽屉打开期间, 被摘掉 `backdrop-filter` 的那些元素。
 *
 * key = 那个元素本身; value = { count, cssText }
 *   · `count`  = 当前有几个抽屉实例把它摘了 (同一个 modal 里可能有 2 个 picker 实例)
 *   · `cssText`= 摘之前的**内联** style 原文 ⇒ 关抽屉时原样还回去 (一个字符都不差)
 *
 * ⚠️ 为什么需要它 —— 见 `_neutralizeModalGlass()` 的注释 (实测出来的坑)。
 */
const MODAL_GLASS = new WeakMap();

/**
 * v0.19.04 — 「键盘留白」记账 (module 级共享)。
 *
 * key   = 那个 `.Modal-content` 元素
 * value = 改动前的**内联** `padding-bottom` 原文 (一个字符都不差 ⇒ 还原时原样写回)
 *
 * ⚠️ 为什么是 **module 级**、不是每个 picker 实例各存一份:
 *   同一个 modal 里可能挂着**两个** picker 实例 (例: LessonEditorModal 的
 *   trialPicker + normalPicker) ⇒ 两个实例若各存一份「原值」, 后写的那个会把
 *   **前一个已经改过的值** (336px) 当成原值存下来 ⇒ 还原时写回 336px 而不是 7px。
 *   ⇒ 共用一份 + 「先写者记原值」⇒ 还原永远回到最初那个值。
 *
 * ⚠️ 为什么用 `Map` 而不是 `WeakMap`:
 *   还原时要把**全部**记过的元素写回原值 ⇒ 需要能遍历 ⇒ WeakMap 做不到。
 *   （条目在还原时立刻 delete ⇒ 不会长期持有脱离文档的节点。）
 */
const MODAL_KEYBOARD_PAD = new Map();

/**
 * v0.19.14 — 「modal 上边缘钉住」补偿 (module 级共享, 仿 MODAL_KEYBOARD_PAD 的做法)。
 *
 * ## 这是什么
 *   手机端 core 的 `.Modal` 是 `position: relative` (vendor/flarum/core/less/common/Modal.less:11)
 *   + `@phone` 段 `top: 100dvh` ⇒ `.in` 时 `top: 0` ⇒ **完全打开后 `.Modal` 的 rect.top 应当 = 0**。
 *   ⇒ 每帧读一次 rect.top; 若 ≠ 基准值 ⇒ 说明 modal 被别的东西推动了 ⇒ **把它推回去**。
 *
 * ## 为什么是 module 级 (不是每个 picker 实例一份)
 *   同一个 modal 里可能挂着**两个** picker (例: LessonEditorModal 的 trialPicker + normalPicker)
 *   ⇒ 两个实例各跑一份补偿会**互相打架** (一个写 top、另一个按旧基准再算一遍)
 *   ⇒ 共用一份 + 引用计数 (`refs`) ⇒ 最后一个实例销毁时才停。
 *
 * ## ⛔ 补偿的实现约束 (违反即红)
 *   ① **用 inline `style.top` 补偿, ⛔ 不用 transform / translate / filter** ——
 *      那几个属性**会创建包含块** ⇒ 抽屉的 `position: fixed` 会重新锚到 `.Modal`
 *      ⇒ **打坏 v0.19.13 的 `bottom: calc(100% - 100dvh)`**。
 *      `.Modal` 是 `position: relative` ⇒ 改 `top` **不创建包含块** ✓。
 *   ② **必须排除「modal 内部滚动」**: `.ModalManager` 滚动时 `.Modal` 的 rect.top 也会变
 *      (变成负数) ⇒ 那不是错位, 是用户在滚内容。
 *      ⇒ 仅当 `.ModalManager.scrollTop` **与基准相同**、而 `.Modal` 的 rect.top 变了 ⇒ 才补偿。
 *   ③ **必须避开 modal 的开/关动画**: core 的 `.Modal` 有 `top: 100dvh → 0` 的 200ms 动画
 *      ⇒ 动画期间 rect.top 本来就不为 0 ⇒ **只在进入 `.in` 且该过渡结束后**才开始比对。
 *      判据 = `getComputedStyle(modal).top === '0px'` (过渡结束后 computed 值才落回 0px)。
 *   ④ **基准值动态取**, ⛔ 不写死 0 —— 万一某个 modal 的正常态就不是 0, 写死会把正常布局拉歪。
 *   ⑤ **只在手机端** (`matchMedia('(max-width: 767px)')`) + **只在本仓 modal 打开期间**
 *      ⇒ 桌面端与其它扩展的 modal 零影响。
 *
 * ## ⚠️ 为什么 `style.transition` 是「写完立刻还回」而不是「补偿期间一直留着 none」
 *   brief §2.2 要求补偿时临时关掉过渡 (core 的 `transition: top .2s ease-out` 会把补偿
 *   变成一段 200ms 的补间动画)。本实现**确实**临时写 `style.transition = 'none'`,
 *   但**在同一个同步块内就逐字节还回** —— 做法是写完 `top` 之后**强制一次样式重算**
 *   (`void modal.offsetHeight`): 那一次重算里 `top` 的变化是在 `transition: none` 下发生的
 *   ⇒ **不会启动补间**; 随后把 transition 还回原值, 而 `top` 的值没再变 ⇒ 也不会启动补间。
 *
 *   ⛔ **为什么不能「补偿期间一直留着 transition:none」**:
 *      Flarum core 的 `ModalManager.animateHide()` 是**等 `transitionend` 才真正关闭 modal** 的
 *      (`vendor/flarum/core/js/src/common/components/ModalManager.tsx`):
 *        `activeDialogElement.addEventListener('transitionend', afterModalClosedCallback, {once:true})`
 *        然后 `classList.remove('in'); classList.add('out');`
 *      ⇒ 若 `.Modal` 上留着 `transition: none`, **关闭动画不会跑 ⇒ `transitionend` 永远不来
 *        ⇒ `state.close()` 永远不被调 ⇒ modal **卡在屏幕上关不掉**。
 *      ⇒ 过渡必须在**补偿写入的那一刻**临时关掉、随即还回。
 *
 * ## ⚠️ 与诊断探针的契约 (供 `iosKeyboardDiag.js` 读)
 *   补偿写入后, `.Modal` 的 rect.top 已被拉回基准 ⇒ 诊断必须能还原出「补偿前」的值:
 *   ⇒ 补偿把当前偏移挂在 modal 元素的 **JS 扩展属性** `__zsTopPin = { applied, baseline }`
 *     (⚠️ **不是** DOM 属性 ⇒ 不污染 `style` / attribute, 也不影响 G9 的逐字节还原)
 *   ⇒ 诊断侧: `mdlTop = rect.top − applied` (⇒ 恒满足 `mdlTop + applied === rect.top`)。
 */
const MODAL_TOP_PIN = {
  /** 挂着本补偿的 picker 实例数 (引用计数) */
  refs: 0,
  /** 总开关 (验收脚本的「故意把补偿关掉再采一次」用; ⛔ 生产不要动) */
  enabled: true,
  /** 手机端断点 (brief §2.2: 只在手机端动作) */
  mql: null,
  /** rAF 句柄 (0 = 没在跑) */
  rafId: 0,
  /** 当前被钉住的 `.Modal` */
  modal: null,
  /** 该 modal 的 `.ModalManager` (用来读 scrollTop / 判定内部滚动) */
  manager: null,
  /** 监听 `.Modal` 的 class 变化 ⇒ modal 开始关闭时**立刻**还原 */
  observer: null,
  /** 'idle' | 'armed' (等过渡结束) | 'active' (开始比对) */
  state: 'idle',
  /** 基准: 进入 `.in` 且过渡结束后的 rect.top (动态取, ⛔ 不写死 0) */
  baselineTop: 0,
  /** 基准: 同一时刻的 `.ModalManager.scrollTop` */
  baselineScroll: 0,
  /** 我们写进 `style.top` 的偏移量 (px) */
  applied: 0,
  /** 是否写过 (决定要不要还原) */
  wrote: false,
  /** 原始 inline `style.top` 原文 (逐字节还回) */
  origTop: null,
  /** 原始 inline `style.transition` 原文 (逐字节还回) */
  origTransition: null,
  /** 原始 inline `style` 属性整串 (最忠实的还原口径) */
  origStyleAttr: null
};

/** ⛔ 浮点比较容差 (px) —— 小于它就算「没偏移」 */
const TOP_PIN_EPS = 0.5;

/**
 * v0.19.14 — 还原「本补偿动过」的 `.Modal` 的内联样式 (逐字节)。
 */
function topPinRestore() {
  const P = MODAL_TOP_PIN;
  if (P.observer) {
    try {
      P.observer.disconnect();
    } catch (e) {/* noop */}
    P.observer = null;
  }
  const modal = P.modal;
  if (modal) {
    try {
      if (P.wrote) {
        // ⚠️ 先把两个属性按**原值**写回, 再把整个 `style` 属性按**原始字节**还回去
        //   (最严格的口径: 连属性顺序/空白都与接管前逐字节一致)
        modal.style.top = P.origTop === null ? '' : P.origTop;
        modal.style.transition = P.origTransition === null ? '' : P.origTransition;
        if (P.origStyleAttr === null) modal.removeAttribute('style');else modal.setAttribute('style', P.origStyleAttr);
      }
    } catch (e) {/* 节点已脱离文档 */}
    try {
      delete modal.__zsTopPin;
    } catch (e) {/* noop */}
  }
  P.modal = null;
  P.manager = null;
  P.state = 'idle';
  P.applied = 0;
  P.wrote = false;
  P.origTop = null;
  P.origTransition = null;
  P.origStyleAttr = null;
}

/**
 * v0.19.14 — 写一次补偿 (inline `style.top`)。
 *
 * @param {HTMLElement} modal
 * @param {number} next 新的偏移量 (px)
 */
function topPinWrite(modal, next) {
  const P = MODAL_TOP_PIN;
  const origTransition = modal.style.transition;
  try {
    // ① 临时关掉过渡 (core 的 `transition: top .2s ease-out` 会把补偿变成 200ms 补间)
    modal.style.transition = 'none';
    // ② 写补偿量
    modal.style.top = next + 'px';
    // ③ ⭐ 强制一次样式重算 ⇒ 这次 top 的变化在 `transition: none` 下发生 ⇒ **不启动补间**
    void modal.offsetHeight;
  } catch (e) {
    // 节点可能已经脱离文档 ⇒ 放弃这一次写入
    return;
  } finally {
    // ④ 逐字节还回过渡 (见上面的注释: 不能一直留着 none, 否则 modal 关不掉)
    try {
      modal.style.transition = origTransition;
    } catch (e) {/* noop */}
  }
  P.applied = next;
  P.wrote = true;
  // 供诊断探针还原「补偿前」的几何 (见 iosKeyboardDiag.js 的契约说明)
  try {
    modal.__zsTopPin = {
      applied: next,
      baseline: P.baselineTop
    };
  } catch (e) {/* noop */}
}

/**
 * v0.19.14 — 监听 `.Modal` 的 class 变化。
 * modal 开始关闭时 (`.in` 被摘掉 / 出现 `.out`) **立刻**还原 ——
 * 否则我们的 inline `top` 会盖住 core 的 `top: 100dvh` ⇒ 关闭动画变成「原地淡出」。
 */
function topPinObserve(modal) {
  if (typeof window.MutationObserver !== 'function') return null;
  try {
    const obs = new window.MutationObserver(() => {
      const P = MODAL_TOP_PIN;
      if (!P.modal) return;
      if (!P.modal.classList.contains('in') || P.modal.classList.contains('out')) {
        topPinRestore();
      }
    });
    obs.observe(modal, {
      attributes: true,
      attributeFilter: ['class']
    });
    return obs;
  } catch (e) {
    return null;
  }
}

/**
 * v0.19.14 补记 — ⭐ **「偏移归零 ⇒ 把内联 `top` 还原掉」**
 *
 * ## 为什么 (验收方独立复核 L3)
 *   旧写法: 偏移归零时 `topPinWrite(modal, 0)` ⇒ **内联样式里留下一行 `top: 0px`**,
 *   要等 modal 关闭才逐字节还原 ⇒ 与 brief §3.2「补偿**不生效时** ⇒ DOM 一字不改
 *   (不写 inline style)」**不完全相符** (触发过一次之后就不满足了)。
 *
 * ## 做法
 *   归零时**不写 `0px`**, 而是把内联 `top` **还原成接管前的原值**,
 *   并在「接管前根本没有 `style` 属性」时把**空壳 `style=""` 一并摘掉**
 *   ⇒ DOM 回到与接管前**逐字节一致**。
 *   ⚠️ **不动补偿的接管状态** (state 仍是 'active'、observer 仍在) ⇒
 *      外部推动再次出现时照样能重新补偿。
 *
 * @param {HTMLElement} modal
 */
function topPinRelease(modal) {
  const P = MODAL_TOP_PIN;
  if (modal && P.wrote) {
    const origTransition = modal.style.transition;
    try {
      // ⚠️⚠️ **过渡必须临时关掉** (同 topPinWrite):
      //   core 的 `.Modal` 在 @phone 有 `transition: top .2s ease-out`
      //   ⇒ 把内联 `top` 拿掉 ⇒ computed top 从 -40px 变回 0px ⇒ **会启动 200ms 补间**
      //   ⇒ 补间期间补偿每帧都读到「半截值」⇒ 互相打架 (实测: 停在 -12.1094px 不收敛)
      //   ⇒ 在 `transition: none` 下改 + 强制重算 ⇒ **不启动补间**。
      modal.style.transition = 'none';
      modal.style.top = P.origTop === null ? '' : P.origTop;
      void modal.offsetHeight; // 强制一次样式重算 ⇒ 这次变化不启动补间
    } catch (e) {/* 节点可能已经脱离文档 */}
    try {
      modal.style.transition = origTransition;
    } catch (e) {/* noop */}
    try {
      // ⭐ **必须放在最后**: 上面每写一次 `style.xxx` 都会把 `style` 属性**重新造出来**
      //   (哪怕写的是空串) ⇒ 摘空壳要等所有写都结束 (口径与 topPinRestore 一致)。
      if (P.origStyleAttr === null) {
        // 接管前**没有** style 属性 ⇒ 现在也不该有 (⛔ 不留 `style=""` 空壳)
        const attr = modal.getAttribute('style');
        if (attr === null || attr.replace(/\s/g, '') === '') modal.removeAttribute('style');
      } else {
        modal.setAttribute('style', P.origStyleAttr);
      }
    } catch (e) {/* noop */}
  }
  P.applied = 0;
  P.wrote = false;
  if (modal) {
    // 供诊断探针还原「补偿前」的几何 (契约见文件头)
    try {
      modal.__zsTopPin = {
        applied: 0,
        baseline: P.baselineTop
      };
    } catch (e) {/* noop */}
  }
}

/**
 * v0.19.14 — 每一帧的补偿判定。
 */
function topPinTick() {
  const P = MODAL_TOP_PIN;
  if (!P.enabled) return;

  // ① 找**本仓**的 modal (`.ZS-agent-search` 标记 ⇒ core 的 modal 与其它扩展的 modal 一律不碰)
  const anchor = document.querySelector('.ZS-agent-search');
  const modal = anchor && anchor.closest ? anchor.closest('.Modal') : null;
  const manager = modal && modal.closest ? modal.closest('.ModalManager') : null;
  if (!modal || !manager) {
    if (P.modal) topPinRestore();
    return;
  }
  // ② modal 换了 (栈里换了另一个) ⇒ 先还原旧的
  if (P.modal && P.modal !== modal) topPinRestore();
  // ③ 没打开 (`in` 没挂上) ⇒ 还原并停手
  if (!modal.classList.contains('in')) {
    if (P.modal) topPinRestore();
    return;
  }
  if (P.modal !== modal) {
    P.modal = modal;
    P.manager = manager;
    P.state = 'armed';
    P.applied = 0;
    P.wrote = false;
    P.origTop = modal.style.top;
    P.origTransition = modal.style.transition;
    P.origStyleAttr = modal.getAttribute('style');
    P.observer = topPinObserve(modal);
  }
  if (P.state === 'armed') {
    // ⚠️ 必须避开 modal 的**打开动画** (`top: 100dvh → 0`, 200ms)
    //   ⇒ 等过渡结束 (computed top 落回 '0px') 才开始比对
    let top = '';
    try {
      top = window.getComputedStyle(modal).top;
    } catch (e) {
      top = '';
    }
    if (top !== '0px') return;
    // ⚠️ 基准值**动态取** (⛔ 不写死 0)
    P.baselineTop = modal.getBoundingClientRect().top;
    P.baselineScroll = manager.scrollTop;
    P.state = 'active';
  }
  if (P.state !== 'active') return;

  // ⚠️ 排除「modal 内部滚动」: scrollTop 变了 ⇒ 用户在滚内容 ⇒ **不补偿** (宁可不动)
  if (manager.scrollTop !== P.baselineScroll) return;
  const cur = modal.getBoundingClientRect().top;
  if (Math.abs(cur - P.baselineTop) < TOP_PIN_EPS) return; // 没有偏移 ⇒ 一个字都不改

  // 写回基准: 需要再挪 (baseline - cur) ⇒ 累加到当前偏移上
  const next = P.applied + (P.baselineTop - cur);
  // ⭐ v0.19.14 补记: 偏移归零 ⇒ **不写 `top: 0px`** ⇒ 把内联 top 还原掉
  //   (brief §3.2「补偿不生效时 ⇒ DOM 一字不改」; 详见 topPinRelease 的注释)
  if (Math.abs(next) < TOP_PIN_EPS) {
    topPinRelease(modal);
    return;
  }
  topPinWrite(modal, next);
}

/** v0.19.14 — 补偿的 rAF 循环 */
function topPinLoop() {
  const P = MODAL_TOP_PIN;
  P.rafId = window.requestAnimationFrame(topPinLoop);
  try {
    topPinTick();
  } catch (e) {/* ⛔ 补偿绝不能把页面搞崩 */}
}

/** v0.19.14 — 按「有没有实例 + 是不是手机端」决定跑不跑循环 */
function topPinSyncLoop() {
  const P = MODAL_TOP_PIN;
  const want = P.refs > 0 && P.enabled && !!(P.mql && P.mql.matches);
  if (want && !P.rafId) {
    P.rafId = window.requestAnimationFrame(topPinLoop);
  } else if (!want && P.rafId) {
    window.cancelAnimationFrame(P.rafId);
    P.rafId = 0;
    topPinRestore();
  }
}

/** v0.19.14 — picker 挂上来 (mount) */
function topPinAttach() {
  const P = MODAL_TOP_PIN;
  P.refs += 1;
  if (!P.mql && window.matchMedia) {
    P.mql = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      topPinSyncLoop();
    };
    if (P.mql.addEventListener) P.mql.addEventListener('change', onChange);else if (P.mql.addListener) P.mql.addListener(onChange); // 老 Safari
  }
  topPinSyncLoop();
}

/** v0.19.14 — picker 摘掉 (destroy) ⇒ 最后一个实例走 ⇒ 停循环 + 还原 */
function topPinDetach() {
  const P = MODAL_TOP_PIN;
  P.refs = Math.max(0, P.refs - 1);
  if (P.refs === 0) {
    topPinSyncLoop();
    topPinRestore();
  }
}

/**
 * v0.19.14 补记 — ⭐ **对外只暴露「只读状态」** (验收方独立复核 L1)
 *
 * ## 为什么要收窄
 *   旧写法: 模块加载即定义 `window.__zsModalTopPin`, **全站每一页都挂**,
 *   而且它带着 `disable()` / `enable()` ⇒ **生产环境任何人打开控制台就能把补偿关掉**,
 *   与源码注释「⛔ 生产不要动」**相悖**。
 *
 * ## 现在的口径
 *   · `state` / `baseline` / `applied` / `wrote` —— **只读状态**, 一直存在
 *     (验收脚本的 G8 桌面轮要读「补偿状态」⇒ 这个只读视图必须一直在)。
 *   · `disable()` / `enable()` —— **只在诊断开关打开时** (`?zskbdiag=1`) 才存在。
 *     用 **getter 现挂**: 访问时**当场**判一次开关 ⇒
 *       ① 生产 (没带参数) ⇒ `__zsModalTopPin.disable` 是 `undefined` ⇒ 控制台关不掉补偿;
 *       ② 探针 teardown 之后 (参数掉了) ⇒ 同样自动消失。
 *   ⚠️ 验收脚本的 `JS_DISABLE_PIN` / `JS_ENABLE_PIN` 用的是 `?zskbdiag=1` 的页面 ⇒ 那两处照常可用。
 */
if (typeof window !== 'undefined') {
  const api = {
    get state() {
      return MODAL_TOP_PIN.state;
    },
    get baseline() {
      return MODAL_TOP_PIN.baselineTop;
    },
    get applied() {
      return MODAL_TOP_PIN.applied;
    },
    get wrote() {
      return MODAL_TOP_PIN.wrote;
    }
  };
  const debugOnly = {
    configurable: true,
    enumerable: false,
    get() {
      // ⛔ 诊断开关没开 ⇒ **不提供**「关掉补偿」的能力
      if (!isIosKeyboardDiagEnabled()) return undefined;
      return function () {
        MODAL_TOP_PIN.enabled = false;
        topPinSyncLoop();
        topPinRestore();
      };
    }
  };
  const debugOnlyEnable = {
    configurable: true,
    enumerable: false,
    get() {
      if (!isIosKeyboardDiagEnabled()) return undefined;
      return function () {
        MODAL_TOP_PIN.enabled = true;
        topPinSyncLoop();
      };
    }
  };
  Object.defineProperty(api, 'disable', debugOnly);
  Object.defineProperty(api, 'enable', debugOnlyEnable);
  window.__zsModalTopPin = api;
}
export default class SearchableUserPicker {
  constructor(opts) {
    if (opts === void 0) {
      opts = {};
    }
    /** 宿主 Modal (只需要 .element; 组件**不改**它的 form) */
    this.host = opts.host || null;
    /** { key: cfg } — 每个 key 一个下拉 */
    this.fields = opts.fields || {};
    /** (key) => string — 读当前选中值 (存字符串, 空串 = 未选) */
    this.getValue = opts.getValue || (() => '');
    /**
     * (key, stringValue, item|null) => void — 写回选中值。
     *
     * v0.17.12 新增第三个参数 `item`: 选中时传入**候选项对象本身**, 清除时传 null。
     * ⚠️ 向后兼容: 老调用方 (trialPicker / SalesFollowupCreateModal) 的
     *   `setValue: (key, v) => {...}` 只用前两个, 多传一个实参在 JS 里完全无害,
     *   两处行为一字不变。只有「值本身就是对象」的宿主 (约课 modal 的教练/学员) 才会用它。
     */
    this.setValue = opts.setValue || (() => {});
    /** () => id|null — 当前校区, 用于候选过滤 */
    this.getSchoolId = opts.getSchoolId || (() => null);
    /**
     * v0.17.12 新增: (key) => item|null — 宿主持有的**当前选中对象**。
     * 只在「候选列表里找不到当前值」时用于**回显名字**, 绝不会把它塞进候选列表。
     * @see selectedUser()
     */
    this.currentObjectOf = opts.currentObject || null;
    /** 列表最多渲染多少条 (后端最多返 200, 太多会把浮动层撑得过长) */
    this.maxVisible = opts.maxVisible || 60;
    this.redraw = opts.redraw || (() => {
      if (typeof m !== 'undefined') m.redraw();
    });

    /**
     * v0.17.13 新增 (可选, **默认 false = 改前行为逐字节不变**):
     * 免疫「宿主 modal 打开时那次**程序化** focus」。
     *
     * ## 它挡的是谁
     *   `ziven-core/js/src/forum/index.js:399` (v0.5.8) 全局 override 了
     *   `Modal.prototype.onready`, 对**所有** modal 强制 `$el.find('input, select, textarea')
     *   .first().trigger('focus').trigger('select')` —— 那是修 iOS 14+ modal 打开跳回顶部。
     *   所以本 modal 的**第一个** input 一定会在打开瞬间被程序化聚焦一次。
     *   v0.17.11 时第一个 input 是「计费课时」的 number input (聚焦无副作用);
     *   v0.17.12 把教练/学员换成 `<input type="text">` 后**教练框变成第一个 input**,
     *   于是那次程序化 focus 直接触发本页的 `onFocusOpen` → 下拉自动弹开, 用户没点过它。
     *
     * ## 判据 (v0.17.25 改写; v0.17.13 原版见下方「v0.17.13 原判据」)
     *   mount() 时**上膛** (`_awaitingFirstUserInput = true`)。解除上膛的**唯一**时机是
     *   「宿主 modal 那次程序化 focus 自己到达」, 即: 一次落在本 modal 内、且用户没有在
     *   那个节点上落过手的 focus。它到达时顺手 `return` 掉那次开列表, 上膛到此为止。
     *
     *   ⚠️ v0.17.13 原判据是「首个 trusted 用户输入之前」—— **顺序假设错了**:
     *   那次程序化 focus 是挂在 `.Modal` 的 `transitionend` 上异步触发的
     *   (ModalManager.animateShow → `onready`, 实测 mount→focus ≈ 250~320ms),
     *   **完全可能排在用户第一次点击之后**。原判据一旦被用户的第一次 mousedown 解除上膛,
     *   那次仍在途的程序化 focus 就没人管了: 它把焦点抢到 modal 的第一个 input (教练框),
     *   学员框因此 blur → 刚点开的列表被 closeDrop 关掉, 教练框反而弹起列表。
     *   表现 = 「点学员没反应, 反而教练那栏自己展开了」。v0.17.25 就是修这个。
     *
     *   现在 mousedown / touchstart **不再**解除上膛, 改为只记 `e.target` 进
     *   `_userTouched`。于是「用户点过的框」拿到 focus 时照常展开 (真 focus),
     *   「用户没碰过的框」拿到 focus 时判定为程序化 → 抑制并解除上膛。
     *   keydown 仍然直接解除上膛 —— Tab 的 keydown 必然先于 focus 到达, 这条不变。
     *
     * ## 为什么不是 `e.isTrusted` (brief 候选 ①, 实测**证伪**)
     *   Chromium 里脚本调的 `HTMLElement.focus()` 产生的事件 `isTrusted` **就是 true**
     *   (`isTrusted` 只区分「浏览器生成」vs `dispatchEvent` 合成)。实测 (2026-10-13, 本地):
     *     ziven-core 自动聚焦  isTrusted = true   (栈里是 jQuery `Object.trigger`)
     *     Tab 键移进输入框    isTrusted = true
     *   两者一模一样 → 用它做判据**分不开**, 修不了 bug。
     *
     * ## 为什么不是「时间窗 / 一次性豁免」 (brief 候选 ②, 实测**否决**)
     *   `Modal.oncreate` 走的是 `this.attrs.animateShow(() => this.onready())`, 而
     *   Flarum `ModalManager.animateShow` (ModalManager.tsx:180) 把回调挂在
     *   **`.Modal` 的 `transitionend`** 上 —— 是**异步**的, 由 CSS 过渡时长决定。
     *   实测间隔: modal DOM 出现 (≈mount) **60.0ms** → `.Modal` transitionend **263.2ms**
     *   → 程序化 focus **263.7ms**, 即 **mount → focus = 203.7ms**。
     *   且 `{once:true}` 会被 `.Modal` 上**任意属性**的过渡结束抢跑 (本次 opacity 263.2ms
     *   就先于 transform 264.3ms)。改主题 / 降级动画 / 慢机器都会让这个数漂移,
     *   窗口写多少都没有依据 → 弃用。
     *
     * ## Tab 键为什么不受影响 (这是本开关唯一的用户感知代价, 已实测归零)
     *   用户按 Tab 时, **trusted 的 keydown 必然先于 focus 到达** —— 实测
     *   keydown **306.7ms** → focus **309.4ms**, 中间隔 2.7ms。keydown 一到就解除上膛,
     *   随后的 focus 照常开列表。所以「Tab 移进搜索框自动展开」**一字不变**。
     *   监听挂在 **window + 捕获阶段**: window 是捕获路径第一站, 一定早于
     *   focus-trap 的 keydown 处理, 杜绝「focus-trap 先处理完才轮到我」的竞态。
     */
    this.suppressAutofocusOpen = opts.suppressAutofocusOpen === true;
    /** v0.17.13 — 上膛标志。mount() 上膛, 解除时机见 v0.17.25 下面那段。 */
    this._awaitingFirstUserInput = false;
    /**
     * v0.17.25 — 上膛期间用户**落手过**的那些 DOM 节点 (Set, mount() 时清空)。
     * 用途只有一个: 判断「这次 focus 是不是宿主 modal 那次程序化 focus」。
     * 记的是**所有** trusted mousedown/touchstart 的 target, 不只是本组件的 input ——
     * 因为 relatedTarget 只要是用户碰过的节点, 就一律按用户操作处理 (失焦照常关列表)。
     */
    this._userTouched = new Set();

    // ── v0.19.01 — 移动端「底部抽屉」形态 (⛔ 与桌面端的 s.open **完全独立**) ──
    //
    // ## 为什么必须是**独立**状态 (brief §4.1)
    //   桌面端 `s.open` = 「就地小浮层(.ZS-agent-drop)展开了」;
    //   移动端 `_sheetKey` = 「底部抽屉(.ZS-agent-sheet)打开了」。
    //   两者**互斥**: 移动端开着抽屉时 `s.open` 必须保持 false (否则两个同时渲染 ⇒ A1 挂)。
    //   ⛔ 绝不复用 `s.open` —— 桌面端那条路径一个字都不许动。
    //
    // ## 抽屉挂在哪儿 (brief §2.2)
    //   **留在 modal 内部**渲染 (不挂 document.body):
    //   core 的 focus trap 是 `createFocusTrap(el, {allowOutsideClick:true})` ——
    //   它只放开**点击**、**不放开焦点** ⇒ 挂到 body 的话底部搜索框**根本聚焦不了**。
    //   ⇒ 抽屉渲染在 `.ZS-agent-search` 里 ⇒ 焦点天然可用 ⇒ 一行 focus-trap 代码都不用碰。
    //
    // ## 为什么 `position: fixed` 不会把抽屉接管/裁掉 (brief §2.1)
    //   core 只在 `@media @phone` 里给 `.Modal { transform: none !important }` ——
    //   没有 transform ⇒ fixed 的包含块 = **视口** ⇒ `bottom:0` 就是屏幕底,
    //   且**不会被** `.Modal-content` 的 `overflow:hidden` 裁掉。
    //   ⇒ 桌面段(`.Modal` 仍带 transform)绝不能渲染抽屉 ⇒ 见 `_computePhoneLayout()`。
    /** @type {string|null} 当前开着抽屉的字段 key (null = 没开) */
    this._sheetKey = null;
    /** 抽屉标题 (bare 宿主没有 cfg.label ⇒ 打开那一刻从 DOM 里读宿主的 label) */
    this._sheetTitle = '';
    /** 键盘/视口动态值: bottom 偏移(px) + 收缩后的 max-height(px|null) */
    this._sheetViewport = {
      bottom: 0,
      maxHeight: null
    };
    /** 断点监听 (brief §4.9: (max-width: 991.98px)) */
    this._mql = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 991.98px)') : null;
    this._onMediaChange = this.onMediaChange.bind(this);
    this._onWindowResize = this.onWindowResize.bind(this);
    this._onVisualViewport = this.onVisualViewport.bind(this);
    /** 缓存的「当前是不是移动端抽屉形态」+ 失效标志 (见 _ensurePhoneLayout) */
    this._phoneLayout = false;
    this._phoneLayoutStale = true;
    /** visualViewport 监听是否已挂 (成对摘, 本仓铁律) */
    this._vvAttached = false;
    /**
     * v0.19.04 — 本实例**动过** padding-bottom 的那些 `.Modal-content`
     * (用来精确还原; 原值本身记在 module 级的 MODAL_KEYBOARD_PAD 里)。
     */
    this._padEls = new Set();

    /** key -> { query, open, list, loaded, suppressFocusOpen, escKeyup } */
    this._state = {};
    Object.keys(this.fields).forEach(key => {
      this._state[key] = {
        query: '',
        open: false,
        list: [],
        loaded: false,
        // v0.16.6 — 见顶部 ③: 外部点击后 focus trap 会把焦点还给输入框, 这一次 focus 不开列表
        suppressFocusOpen: false,
        // v0.16.6 — 见顶部 ②: Esc 的 keydown / keyup 分两次记账, 否则 keyup 会把弹窗也带走
        escKeyup: false
      };
    });
    this._onDocMousedown = this.onDocMousedown.bind(this);
    // v0.17.13 — 解除上膛。捕获阶段 + window, 保证早于 focus-trap 的 keydown 处理。
    this._onFirstUserInput = this.onFirstUserInput.bind(this);
  }

  // ── 生命周期 ────────────────────────────────────────────────────

  /** oncreate 里调: 挂 document 监听 (候选由 loadAll 单独拉, 见下) */
  mount() {
    if (!this._mounted) {
      document.addEventListener('mousedown', this._onDocMousedown);
      // v0.19.01 — 断点 / 视口监听 (成对摘, 见 destroy)。
      //   · matchMedia('(max-width: 991.98px)') 的 change ⇒ 断点变化 (brief §4.9)
      //   · window resize ⇒ 缓存的「移动端形态」失效 (transform:none 只在 core 的 @phone 段成立)
      if (this._mql) {
        if (this._mql.addEventListener) this._mql.addEventListener('change', this._onMediaChange);else if (this._mql.addListener) this._mql.addListener(this._onMediaChange); // 老 Safari
      }
      window.addEventListener('resize', this._onWindowResize);
      this._invalidatePhoneLayout();
      // v0.19.14 — 「modal 上边缘钉住」补偿: 上膛 (⛔ 批 24 的留白残留本批不动)
      topPinAttach();
      // v0.17.13 — 上膛。宿主 Modal.oncreate 是 `super.oncreate()` 先跑 (同步注册
      //   transitionend), 再调本组件 mount(); transitionend 至少隔一帧才会来,
      //   所以这里上膛**一定早于** ziven-core 那次程序化 focus。
      if (this.suppressAutofocusOpen) {
        this._awaitingFirstUserInput = true;
        this._userTouched.clear();
        window.addEventListener('keydown', this._onFirstUserInput, true);
        window.addEventListener('mousedown', this._onFirstUserInput, true);
        window.addEventListener('touchstart', this._onFirstUserInput, true);
      }
      this._mounted = true;
    }
  }

  /** onremove 里调: 必须摘, 否则 modal 关掉后监听还活着, 每次全站 mousedown 都跑进死实例 */
  destroy() {
    if (this._mounted) {
      document.removeEventListener('mousedown', this._onDocMousedown);
      // v0.19.01 — 抽屉相关的监听必须摘干净 (本仓铁律: 监听必须成对摘)
      this.closeSheet(null, true); // silent: 组件要没了, 不用 redraw
      if (this._mql) {
        if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._onMediaChange);else if (this._mql.removeListener) this._mql.removeListener(this._onMediaChange);
      }
      window.removeEventListener('resize', this._onWindowResize);
      // v0.19.14 — 补偿下膛 (最后一个实例走时 ⇒ 停循环 + 逐字节还原 `.Modal` 的内联样式)
      topPinDetach();
      // v0.17.13 — 新增的监听必须成对摘掉 (v0.17.25 起是四个)
      if (this.suppressAutofocusOpen) {
        this._releaseOpeningGuard();
      }
      this._mounted = false;
    }
  }

  /** onupdate 里调: 每轮 patch 后重新判定要不要向上翻 (下拉是这轮刚插进来的) */
  afterUpdate() {
    this.updateFlips();
  }

  // ── 候选 ────────────────────────────────────────────────────────

  /** 拉所有下拉的候选。通常在宿主 load() 里 await, 让 loading 态覆盖到候选就绪。 */
  async loadAll() {
    await Promise.all(Object.keys(this.fields).map(key => this.loadCandidates(key)));
  }

  /**
   * v0.17.12 — 取某个候选项的主键。走 `cfg.idOf`, 默认 `item => item.id`。
   *
   * ⚠️ 为什么要有这个口子: `/users` 返的项主键叫 `id`, 而 `students-list` 返的项主键叫
   *   `user_id` (它的 `id` 字段只是恰好也等于 users.id)。宿主自己的 submit payload 与
   *   loadStudentCards 都是 `user_id || id` 口径, 统一由宿主传 idOf 进来, 组件内不再各写一份。
   *   不传 idOf 的老调用方拿到的仍是 `item.id`, 行为零变化。
   */
  idOfItem(key, item) {
    const cfg = this.fields[key] || {};
    const fn = cfg.idOf || (x => x.id);
    return fn(item);
  }

  /**
   * 拉某个下拉的候选源。
   *
   * `defaultCurrentUser`: 候选里有当前登录人时预选, 省得每次都展开点一下。
   * 预选只在**当前值仍为空**时生效, 不会覆盖调用方已有的值。
   * ⚠️ v0.16.9 起销售跟进 modal **不传**这个开关 (辉哥要求留空自选), 能力本身保留,
   * 给以后需要「默认选中我」的新调用方用。
   */
  async loadCandidates(key) {
    const cfg = this.fields[key];
    if (!cfg) return [];
    const st = this._state[key];

    // v0.17.12 — 宿主自带候选源 (cfg.pick): 同步赋值, 不碰网络。
    //   约课 modal 的教练/学员候选在 load() 里已经拉好了, 直接复用 = 零新增请求。
    //   这条分支里一个 await 都没有, 所以 pick() 是同步执行的。
    if (typeof cfg.pick === 'function') {
      // v0.18.38 — 同样要去重 (理由见 dedupeById 的注释)。
      st.list = dedupeById(cfg.pick() || [], u => this.idOfItem(key, u));
      st.loaded = true;
      return st.list;
    }
    const schoolId = this.getSchoolId();
    try {
      const users = await Api.list('users', {
        role: cfg.role,
        ...(schoolId ? {
          school_id: +schoolId
        } : {})
      }).catch(() => []);
      // v0.18.38 — ⛔ 必须去重: 后端 /users 对「同时命中多个角色组」的人会返**重复行**。
      //   详见 dedupeById 的注释 (那不是洁癖, 是 mithril keyed diff 的**崩溃**)。
      st.list = dedupeById(users || [], u => this.idOfItem(key, u));
    } catch (e) {
      console.error('[searchable-user-picker] load candidates failed', key, e);
      st.list = [];
    }
    st.loaded = true;
    if (cfg.defaultCurrentUser && !this.getValue(key)) {
      const me = app.session.user && +app.session.user.id();
      if (me && st.list.some(u => +this.idOfItem(key, u) === me)) {
        this.setValue(key, String(me));
      }
    }
    return st.list;
  }

  // ── 选中态 ──────────────────────────────────────────────────────

  userName(u) {
    return u && (u.display_name || u.username) || '';
  }

  /**
   * v0.18.100 — 把一个候选条目适配成 core `<Avatar>` 能用的「小 User 对象」。
   *
   * ## 为什么用 adapter, 而不是 store 里的 User model
   *   core 的 `<Avatar>` 只用到 User 的 **4 个**方法
   *   (`displayName()` / `avatarUrl()` / `avatarSrcset?.()` / `color()`,
   *    见 vendor/flarum/core/js/src/common/components/Avatar.tsx)
   *   ⇒ 一个「长得像 User 的小对象」就够用。
   *   ⚠️ **core 升级时若给 Avatar 新增了方法, 要同步补这个 adapter。**
   *
   * ## ⛔ 为什么**不能** `app.store.push(...)`
   *   那会把**残缺的** User model 塞进**全局** store ⇒ 别处 `app.store.find('users', id)`
   *   因为 store 里已经有该 id 而**不发请求** ⇒ 拿到残缺 model ⇒ 波及全站。
   *   ⛔ 也**不能**逐个 `app.store.find('users', id)` 去拉:
   *      core 的 `/api/users?filter[id]=` **不支持批量** —— 实测 `?filter[id]=2,101,67`
   *      返回的是**默认 20 条**, 不是这 3 个人 ⇒ 只能一个个拉 ⇒ 候选几十个就是几十个请求。
   *
   * ## 数据来源: **零网络请求**
   *   两个候选源**本来就自带** `avatar_url` (实测: 值就是**完整 URL**,
   *   如 `http://localhost:8844/assets/avatars/xxx.png`; 没有的人返 `null`):
   *     · /api/ziiven/scheduler/users        → ListUsersByRoleController.php:143 (row())
   *     · /api/ziiven/scheduler/students-list → ListStudentsController.php:169
   *   ⇒ 本组件**不需要**为了头像发任何请求。
   *
   * ## color(): 与 core `User.color()` **同口径**
   *   · 有头像 → 返回 '' (core 自己也这样: 那条分支直接 `return <img>`, 背景色根本看不见)
   *   · 无头像 → `'#' + stringToColor(name)` ⇒ **每人一个颜色** (与消课统计那边一致)
   *   ⚠️ `stringToColor` 返回的是**不带 #** 的 hex, 这里自己补前缀 (同 CalendarTab.js:1493)。
   */
  avatarUser(u, name) {
    const avatarUrl = u.avatar_url || null;
    return {
      displayName: () => name,
      avatarUrl: () => avatarUrl,
      avatarSrcset: () => null,
      color: () => avatarUrl ? '' : '#' + stringToColor(name)
    };
  }

  /**
   * 当前选中的用户对象; 没选返回 null。
   *
   * v0.17.12 — 候选列表里找不到当前值时, 退回宿主的 `currentObject` (edit mode 回显用)。
   * ⚠️ 它**只用于回显** (输入框 placeholder 显示名字 + 清除按钮出现),
   *   绝不会被塞进 `st.list` —— 所以「展开后候选集」永远等于宿主给的数组, 逐条一致。
   *   不传 currentObject 的老调用方拿到的仍是纯 list.find, 行为零变化。
   */
  selectedUser(key) {
    const id = +this.getValue(key);
    if (!id) return null;
    const st = this._state[key];
    const found = st.list.find(u => +this.idOfItem(key, u) === id) || null;
    if (found) return found;
    return this.currentObjectOf ? this.currentObjectOf(key) : null;
  }

  /** 候选按搜索词过滤。空词返回全部 —— 打开就是完整名单, 不用先打字。 */
  filtered(key) {
    const st = this._state[key];
    const q = String(st.query || '').trim().toLowerCase();
    if (!q) return st.list;
    return st.list.filter(u => this.userName(u).toLowerCase().indexOf(q) !== -1);
  }
  choose(key, u) {
    // v0.17.12 — 第三个参数把候选项**对象本身**交给宿主 (值存对象的宿主需要它)。
    //   老调用方的 setValue 只声明 (key, v), 多传一个实参完全无害。
    this.setValue(key, String(this.idOfItem(key, u)), u);
    this._state[key].query = '';
    this.closeDrop(key);
    // v0.19.01 — 移动端: 选中后**自动关抽屉** (brief §4.6)。
    //   ⛔ 桌面端这一句是**空操作** (桌面端 `_sheetKey` 恒为 null ⇒ 直接 return)。
    this.closeSheet(key);
  }
  clear(key) {
    this.setValue(key, '', null);
    this._state[key].query = '';
    this.closeDrop(key);
    // v0.19.01 — 移动端: 清除也要关抽屉 (brief §4.8)。桌面端同样是空操作。
    this.closeSheet(key);
  }

  // ── v0.19.01 — 移动端「底部抽屉」 ──────────────────────────────────
  //
  // ⚠️ 本节**只**服务移动端。桌面端那套 (`s.open` + `.ZS-agent-drop`) 一行都没动。

  /**
   * v0.19.01 — 当前是不是「移动端抽屉形态」。
   *
   * 判据 = brief §4.9 的断点 **(max-width: 991.98px)** ∧ brief §2.1/§2.3 的**机制前提**:
   *   抽屉是 `position: fixed` ⇒ 它的包含块必须是**视口** ⇒ 祖先 `.Modal` 不能带 transform。
   *   core 只在 `@media @phone` (core 自己的 @phone = max-width: 767.98px) 里给
   *   `.Modal { transform: none !important }`; 而 768~991.98px 那段 `.Modal` 仍带
   *   `transform: scale(1)` ⇒ fixed 会被 `.Modal` 接管、并被 `.Modal-content` 的
   *   `overflow: hidden` **看不见地裁掉** ⇒ 那儿一律走**原来的就地小浮层**(零回归)。
   *
   * ⚠️ 这不是新口径 —— 它就是 brief §2.1/§2.3 自己写下的前提
   *   (「桌面端 .Modal 有 transform ⇒ fixed 会被接管并被裁掉 ⇒ 桌面端绝不能渲染抽屉」),
   *   这里只是把「桌面端」精确成「`.Modal` 还带 transform 的那一段」。
   */
  _computePhoneLayout() {
    if (!this._mql || !this._mql.matches) return false;
    const el = this.host && this.host.element;
    if (!el || !el.closest) return false;
    const modal = el.closest('.Modal');
    if (!modal) return false;
    let t = '';
    try {
      t = window.getComputedStyle(modal).transform || '';
    } catch (e) {
      t = '';
    }
    return t === 'none';
  }

  /** 标脏: 断点/视口一变, 缓存的形态判定就失效了 (transform:none 只在 media query 命中时成立) */
  _invalidatePhoneLayout() {
    this._phoneLayoutStale = true;
  }

  /**
   * 取「当前是不是移动端抽屉形态」(带缓存)。
   * 缓存只在「可能变」的时候重算 (断点/视口变化) ⇒ 正常渲染里**零** getComputedStyle。
   */
  _ensurePhoneLayout() {
    if (!this._phoneLayoutStale) return this._phoneLayout;
    this._phoneLayoutStale = false;
    const next = this._computePhoneLayout();
    if (next !== this._phoneLayout) this._phoneLayout = next;
    return this._phoneLayout;
  }

  /** 断点变化 (brief §4.9): 从手机变桌面 ⇒ **立刻关抽屉** (否则桌面端会留下一个被裁剪的抽屉) */
  onMediaChange() {
    this._invalidatePhoneLayout();
    this._ensurePhoneLayout();
    if (!this._phoneLayout) this.closeSheet();
  }

  /** 视口变化: ① 形态判定失效 ② 键盘高度变了要重算抽屉的 bottom/max-height */
  onWindowResize() {
    this._invalidatePhoneLayout();
    this._ensurePhoneLayout();
    if (!this._phoneLayout) this.closeSheet();
    if (this._sheetKey) this._updateSheetViewport();
  }

  /** visualViewport 变化 (键盘弹起 / 收起 / 页面滚动) */
  onVisualViewport() {
    if (!this._sheetKey) return;
    this._updateSheetViewport();
  }

  /**
   * v0.19.01 — 算键盘高度, 给抽屉一个动态 `bottom` + 收缩后的 `max-height` (brief §4.5)。
   *
   * ⚠️ iOS Safari 的老坑: 键盘弹起时 `position: fixed` 的元素会跳到**可视区顶** ——
   *   所以 bottom 必须用 `visualViewport.offsetTop` 修正:
   *     可视区在布局视口里的下沿 = offsetTop + height
   *     ⇒ 键盘高度 = window.innerHeight - (offsetTop + height)
   *     ⇒ 抽屉的 bottom = 键盘高度 ⇒ 抽屉下沿正好压在**键盘上沿** (搜索框贴着键盘)。
   *
   * ⚠️ 阈值 80px: 手机浏览器地址栏收缩也会让 visualViewport 变矮一点, 那**不算**键盘。
   *
   * v0.19.04 — 额外给「picker 所在 modal 的 `.Modal-content`」加**键盘高度**的下留白
   *   (根因见 `_applyModalKeyboardInset()` 的注释)。
   *   ⛔ `_sheetViewport` 的语义 (bottom / maxHeight 的算法与阈值) **一个字都没改** ——
   *      抽屉的键盘上移行为逐字节不变; 新增的只是内容区的留白。
   */
  _updateSheetViewport() {
    const vv = window.visualViewport;
    let bottom = 0;
    let maxHeight = null;
    let kb = 0;
    if (vv) {
      kb = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      if (kb > 80) {
        bottom = kb;
        // 抽屉最多占「可视区减去键盘」的高度 (CSS 的 70vh 仍然生效 ⇒ 取两者的较小值)
        maxHeight = Math.min(Math.round(vv.height), Math.round(window.innerHeight * 0.7));
      }
    }
    // v0.19.04 — 留白在 early-return **之前**落 ⇒ 即使 bottom/maxHeight 没变也保证留白是对的
    this._applyModalKeyboardInset(kb);
    const cur = this._sheetViewport;
    if (cur.bottom === bottom && cur.maxHeight === maxHeight) return;
    cur.bottom = bottom;
    cur.maxHeight = maxHeight;
    this.redraw();
  }

  /**
   * v0.19.04 — 取「picker 所在 modal 的 `.Modal-content`」。
   *
   * ⛔ **不许**用 `document.querySelector('.Modal-content')` (全局拿第一个) ——
   *   多 modal 叠开时会拿错那个 ⇒ 留白会加到**别人的** modal 上。
   * ⇒ 从**本 picker 自己渲染出来的锚点**往上找 (`.ZS-agent-search[data-zs-picker=…]`)
   *   —— 找不到再退到宿主 element 的祖先链 (宿主 element 本身就是 `.Modal`,
   *   `.Modal-content` 是它的**子**节点 ⇒ 这条退路通常拿不到, 只是兜底)。
   */
  _modalContentEl() {
    const host = this.host && this.host.element;
    if (!host || !host.querySelector) return null;
    const keys = Object.keys(this.fields);
    for (let i = 0; i < keys.length; i++) {
      const anchor = host.querySelector('.ZS-agent-search[data-zs-picker="' + keys[i] + '"]');
      if (anchor && anchor.closest) {
        const mc = anchor.closest('.Modal-content');
        if (mc) return mc;
      }
    }
    return host.closest ? host.closest('.Modal-content') : null;
  }

  /**
   * v0.19.04 — 给「picker 所在 modal 的 `.Modal-content`」加**键盘高度**的下留白。
   *
   * ## 为什么 (iOS 顶部露出页面内容的三段式根因, 命中的是第 ② 段)
   *   ① ziven-core 的 modalScrollGuard 把 body 拉回 `position:static`
   *      ⇒ 打开 modal 期间**页面是可滚的**;
   *   ② iOS 弹键盘 ⇒ visual viewport 收缩 ⇒ 抽屉底部那个输入框落到可视区外
   *      ⇒ 浏览器要把它滚进可视区; 而 `.Modal-content` 正好一屏高
   *      ⇒ `.ModalManager` 不可滚 ⇒ 浏览器只能去**滚页面**;
   *   ③ 页面一滚 ⇒ iOS Safari 把 `position:fixed` 的 `.ModalManager`/`.Modal-backdrop`
   *      锚到 body 而不是视口 ⇒ 整层随页面上移 ⇒ **顶部露出没被压暗的页面内容**。
   *
   * ## 修法 (只动第 ② 段)
   *   给内容区加一段「键盘高度」的留白 ⇒ `.Modal-content` 装得下键盘
   *   ⇒ `.ModalManager` 变可滚 ⇒ 浏览器改滚 **modal 内部**、不滚页面
   *   ⇒ 页面 scrollY 不变 ⇒ ③ 不触发 ⇒ 顶部不再露出页面内容。
   *
   * @param {number} kb 键盘高度 (px); ≤80 视为「键盘没弹起」(与抽屉的阈值同口径)
   */
  _applyModalKeyboardInset(kb) {
    // 键盘收起 / 地址栏收缩 ⇒ 还原 (⛔ 阈值与 `_updateSheetViewport` 同口径: 80)
    if (!(kb > 80)) {
      this._restoreModalKeyboardInset();
      return;
    }
    const el = this._modalContentEl();
    // modal 已关 (picker 还在, modal 已经没了) ⇒ 还原
    if (!el) {
      this._restoreModalKeyboardInset();
      return;
    }
    // ⛔ 原值必须**原样**记住 (不能粗暴清空 ⇒ 会抹掉 core/宿主自己写的内联值)
    if (!MODAL_KEYBOARD_PAD.has(el)) {
      MODAL_KEYBOARD_PAD.set(el, el.style.paddingBottom);
    }
    this._padEls.add(el);
    const want = kb + 'px';
    if (el.style.paddingBottom !== want) el.style.paddingBottom = want;
  }

  /**
   * v0.19.04 — 还原「本实例动过」的 `.Modal-content` 的 padding-bottom。
   *
   * ⚠️ 原值可能是空串 (本来就没有内联 padding) ⇒ **必须原样写回**,
   *   ⛔ 不能 `el.style.paddingBottom = ''` 粗暴清空 (那会把宿主写的内联值也抹掉)。
   */
  _restoreModalKeyboardInset() {
    if (!this._padEls.size) return;
    this._padEls.forEach(el => {
      const orig = MODAL_KEYBOARD_PAD.get(el);
      if (orig === undefined) return; // 已被别的实例还原过 ⇒ 不用再写
      MODAL_KEYBOARD_PAD.delete(el);
      try {
        el.style.paddingBottom = orig;
      } catch (e) {/* 节点已脱离文档 */}
    });
    this._padEls.clear();
  }

  /** 挂 visualViewport 监听 (成对摘, 本仓铁律) */
  _attachViewportListeners() {
    if (this._vvAttached) return;
    this._vvAttached = true;
    const vv = window.visualViewport;
    if (!vv) return;
    if (vv.addEventListener) {
      vv.addEventListener('resize', this._onVisualViewport);
      vv.addEventListener('scroll', this._onVisualViewport);
    } else if (vv.addListener) {
      vv.addListener('resize', this._onVisualViewport);
      vv.addListener('scroll', this._onVisualViewport);
    }
  }

  /** 摘 visualViewport 监听 (成对摘, 本仓铁律) */
  _detachViewportListeners() {
    // v0.19.04 — 「modal 关了而键盘还在」的兜底还原 (⛔ 不依赖 window.beforeunload)。
    //   键盘收起 ⇒ visualViewport.resize ⇒ 走 kb<=80 分支还原;
    //   但直接点 X 关掉 modal 时键盘可能仍在 ⇒ 这里必须还原, 否则留白留在 DOM 上。
    //   ⚠️ 放在 early-return **之前** ⇒ 监听本来就没挂过 (桌面端) 也要保证还原干净。
    this._restoreModalKeyboardInset();
    if (!this._vvAttached) return;
    this._vvAttached = false;
    const vv = window.visualViewport;
    if (!vv) return;
    if (vv.removeEventListener) {
      vv.removeEventListener('resize', this._onVisualViewport);
      vv.removeEventListener('scroll', this._onVisualViewport);
    } else if (vv.removeListener) {
      vv.removeListener('resize', this._onVisualViewport);
      vv.removeListener('scroll', this._onVisualViewport);
    }
  }

  /**
   * v0.19.01 — 打开抽屉 (移动端唯一入口, brief §4.2)。
   *
   * ⛔ **不复用 `s.open`** (brief §4.1) ⇒ 移动端不会同时冒出那个 172px 小浮层。
   * ⛔ **不自动聚焦底部搜索框** (brief §4.2 + 辉哥 10/26 11:46 拍板):
   *   聚焦 = 手机键盘立刻吃掉半屏 ⇒ 用户点底部搜索框才聚焦。
   *   (顺带把 modal 里那个输入框设成 readonly —— 否则用户点它时系统键盘会先弹起来,
   *    抽屉一打开就被键盘顶掉半屏; readonly 的 input 在 iOS 上**不弹键盘**。)
   */
  openSheet(key) {
    if (!this._ensurePhoneLayout()) return;
    const s = this._state[key];
    if (!s) return;
    this._sheetKey = key;
    this._sheetTitle = this._readHostLabel(key);
    s.open = false; // ⛔ 绝不让小浮层同时出现 (A1)
    s.query = ''; // 打开就是完整名单 (与桌面端「空词 = 全部」同口径)
    // v0.19.01 — 摘掉抽屉与 .Modal 之间祖先的 backdrop-filter ⇒ fixed 的包含块 = 视口
    //   (实测: ziven-dress-up 的玻璃风给全站 .Modal-content 加了 backdrop-filter,
    //    不摘的话抽屉会被钉在弹窗内容底边 ⇒ 长表单里整个跑到屏幕外。详见 _neutralizeModalGlass)
    this._neutralizeModalGlass(true);
    this._attachViewportListeners();
    this._updateSheetViewport();
    this.redraw();
  }

  /**
   * v0.19.01 — 关抽屉 (brief §4.7 的四条关闭路径都走它)。
   *
   * @param {string|null} key 只关这个字段的抽屉 (传 null = 不管 key, 全关)
   * @param {boolean} silent 组件销毁时用: 不 redraw
   */
  closeSheet(key, silent) {
    const cur = this._sheetKey;
    if (!cur) return;
    if (key && key !== cur) return;
    // v0.19.01 — 先把 backdrop-filter 还回去 (必须在清 _sheetKey 之前: 那个方法靠它找锚点)
    this._neutralizeModalGlass(false);
    this._sheetKey = null;
    this._sheetTitle = '';
    // v0.16.6 ③ 同款闸: 关抽屉之后, core 的 focus trap 可能把焦点还给 modal 里那个输入框
    //   ⇒ 那一次 focus **不许**把抽屉重开 (否则「怎么点都关不掉」)。
    const s = this._state[cur];
    if (s) s.suppressFocusOpen = true;
    this._detachViewportListeners();
    if (!silent) this.redraw();
  }

  /**
   * v0.19.01 — 摘掉 (或还回) 抽屉与 `.Modal` 之间那些祖先节点的 `backdrop-filter`。
   *
   * ## 为什么必须摘 (这是 brief §2.1 前提的**实测补丁**)
   *   brief §2.1 说: core 手机端 `.Modal` 是 `transform: none !important` ⇒ 抽屉用
   *   `position: fixed` 时包含块 = **视口**。前半句实测成立 (本地 390×844:
   *   `.Modal` computed transform = `none`) —— **但还有第二个包含块来源被漏掉了**:
   *
   *   `ziven-dress-up` 的玻璃风给**全站** `.Modal-content` 加了
   *   `backdrop-filter: var(--seedream-glass-blur) !important` (= blur(20px))
   *   (packages/ziven-dress-up/less/forum.less L2831 的 `#modal .Modal-content, .Modal-content`)
   *   ⇒ **带 backdrop-filter 的元素会成为 fixed 后代的包含块** (跟 transform/filter 同理)
   *   ⇒ 抽屉被钉在**弹窗内容的底边**, 而不是视口底边。
   *
   *   实测 (本地真站, 本批改造后):
   *     · 390×844: 视口底 844, 抽屉底 **857.4** (= `.Modal-content` 底边) ⇒ 差 13.4px
   *     · 900×800: 视口底 800, 抽屉底 **929.4** (= `.Modal-content` 底边) ⇒ **整个抽屉跑到屏幕外**
   *   ⇒ 不摘的话, 抽屉在长表单的弹窗里**根本看不见**。
   *
   * ## 怎么做
   *   抽屉打开期间, 把「抽屉 ↔ `.Modal` 之间」那些祖先节点的 `backdrop-filter` 摘掉
   *   (内联 `!important` ⇒ 能压过 ziven-dress-up 的 `!important`), 关抽屉时**原样还回**
   *   (整段 `cssText` 存/取 ⇒ 一个字符都不差)。
   *   ⚠️ 只摘 backdrop-filter, **不碰** transform/filter/其它任何属性 ⇒ 视觉影响仅限
   *      「弹窗背后的模糊」这一层, 而抽屉的遮罩本身已经盖了 90% ⇒ 肉眼几乎看不出差别。
   */
  _neutralizeModalGlass(on) {
    const host = this.host && this.host.element;
    if (!host || !host.querySelector || !host.closest) return;
    const modal = host.closest('.Modal');
    if (!modal) return;
    const anchor = modal.querySelector('.ZS-agent-search[data-zs-picker="' + (this._sheetKey || '') + '"]');
    const nodes = [];
    let n = anchor ? anchor.parentElement : null;
    while (n && n !== modal) {
      nodes.push(n);
      n = n.parentElement;
    }
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      let st = MODAL_GLASS.get(el);
      if (on) {
        if (!st) {
          st = {
            count: 0,
            cssText: el.style.cssText
          };
          MODAL_GLASS.set(el, st);
        }
        st.count += 1;
        if (st.count === 1) {
          el.style.setProperty('backdrop-filter', 'none', 'important');
          el.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
        }
      } else if (st) {
        st.count -= 1;
        if (st.count <= 0) {
          el.style.cssText = st.cssText; // 原样还回 (含优先级)
          MODAL_GLASS.delete(el);
        }
      }
    }
  }

  /**
   * v0.19.01 — 抽屉标题。
   * ① `cfg.label` (组件自己输出 label 的宿主) ⇒ 「选择」+ label
   * ② bare 宿主 (组件不输出 label, 宿主自己渲染了一个) ⇒ **打开那一刻**从 DOM 里读那个 label
   * ③ 都拿不到 ⇒ 「选择用户」
   * ⚠️ **不新造翻译 key** (brief §0): 全部沿用宿主已有的 label 文案。
   */
  sheetTitle(key) {
    const cfg = this.fields[key] || {};
    if (cfg.label) return '选择' + cfg.label;
    if (this._sheetTitle) return this._sheetTitle;
    return '选择用户';
  }

  /**
   * v0.19.01 — 从 DOM 里读宿主的 label (只给 bare 宿主用)。
   * bare 宿主的结构: `.ZS-form-field` / `.ZS-form-group` 里有一个 `.ZS-form-label`,
   *   紧挨着组件输出的 `.ZS-agent-search` ⇒ 取它去掉图标与必填星号后的纯文字。
   */
  _readHostLabel(key) {
    const el = this.host && this.host.element;
    if (!el || !el.querySelector) return '';
    const anchor = el.querySelector('.ZS-agent-search[data-zs-picker="' + key + '"]');
    if (!anchor || !anchor.closest) return '';
    const box = anchor.closest('.ZS-form-field, .ZS-form-group');
    if (!box || !box.querySelector) return '';
    const lab = box.querySelector('.ZS-form-label');
    if (!lab) return '';
    let t = (lab.textContent || '').replace(/\s+/g, ' ').trim();
    t = t.replace(/[*＊]\s*$/, '').trim(); // 必填星号
    t = t.replace(/(可搜索|选填)\s*$/, '').trim(); // 组件/宿主加的角标与提示
    return t;
  }

  /**
   * v0.19.01 — 角色标签文案 (与桌面端候选项**同一口径**)。
   * ⚠️ 桌面端那一行 JSX 一个字没动 (逐字节不变) ⇒ 这里另写一份同口径的;
   *   改角色文案时两处一起改 (桌面端在 render() 的 `.ZS-agent-opt` 里)。
   */
  roleLabel(u) {
    if (!u) return '';
    if (u.role === 'coach') return '教练';
    if (u.role === 'sales') return '销售';
    if (u.role === 'manager') return '店长';
    return u.role;
  }

  /**
   * v0.19.01 — 命中关键词 `<mark>` 高亮 (设计稿「打字中」场景, brief §3.3)。
   *
   * ## ⚠️⚠️ 这里返回的数组是 mithril 的一个 **fragment** ⇒ 它的**所有**元素
   *   要么**全带 key**、要么**全不带** (mithril 2.x 的硬规则)。
   *
   * ## 🐛 v0.19.01 接手时踩的坑 (真实的线上崩溃, 已修)
   *   原写法给 `<mark>` 加了 `key="zs-hl"`, 而前后两段是**字符串**(字符串没有 key)
   *   ⇒ fragment 变成「有的带 key、有的不带」⇒ mithril 抛:
   *     `TypeError: In fragments, vnodes must either all have keys or none have keys.`
   *   ⇒ **抽屉一打字就崩** ⇒ 列表停在旧状态 (看着就是「搜索框不过滤」)。
   *   ⇒ **修法**: `<mark>` **不要** key —— 三段全是「无 key」, 规则满足。
   *
   * ⚠️ **别再给这里的任何元素加 key** —— 字符串没法带 key, 加了就一定崩。
   *   (想要 key 就得把文本也包成 vnode ⇒ 会改 DOM 结构 ⇒ 与设计稿不符 ⇒ 不做)
   */
  highlightName(name, query) {
    const q = String(query || '').trim();
    if (!q) return name;
    const idx = name.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return name;
    return [name.slice(0, idx),
    // ⚠️ ⛔ 故意**不加** key —— 见上面的注释 (mithril fragment 的 key 规则)
    m("mark", null, name.slice(idx, idx + q.length)), name.slice(idx + q.length)];
  }

  // ── 「开 / 关」 ───────────────────────────────────────────────────
  //
  // ⚠️ v0.16.0 只在 onfocus/oninput 置 true, 选完才置回 false —— 中间没有任何一处因为
  //    失焦 / Esc / 点外部而关闭, 于是列表就一直挂着。v0.16.6 补了下面这三条, 全部走
  //    closeDrop 这一个出口。

  /** 唯一的关闭出口。已关则直接返回, 免得多个来源叠在一起时重复 redraw。 */
  closeDrop(key) {
    const s = this._state[key];
    if (!s || !s.open) return;
    s.open = false;
    s.escKeyup = false;
    this.redraw();
  }

  /** 当前展开着的下拉 key (没展开返回 null)。一个 modal 里同时只允许开一个。 */
  openKey() {
    const keys = Object.keys(this._state);
    for (let i = 0; i < keys.length; i++) {
      if (this._state[keys[i]].open) return keys[i];
    }
    return null;
  }

  /**
   * 点下拉外部关闭。
   *
   * 挂在 document 上 (而不是某个 `.ZS-agent-search`): 用户说的「空白处」命中的是
   * 不可聚焦的元素 (label / 标题 / 留白), 那一刻 input 压根不会 blur, 只能全局兜底。
   *
   * ⚠️ v0.16.7 新增: 只认**当前展开的那个**下拉的容器。开着教练列表点经办人输入框,
   *   经办人也在某个 `.ZS-agent-search` 里 —— 老逻辑 closest() 命中就 early-return,
   *   教练列表会**关不掉**。所以要比对 `data-zs-picker` 是不是展开的那个 key。
   */
  onDocMousedown(e) {
    const openKey = this.openKey();
    if (!openKey) return;
    const t = e.target;
    if (t && t.closest) {
      const box = t.closest('.ZS-agent-search');
      // 点在自己这个下拉里 (含候选项本身) 不算外部。候选项正是靠 onmousedown 的
      // preventDefault 压住失焦后接 click 的, 这里必须放行, 否则「能选中」会被打死。
      if (box && box.getAttribute('data-zs-picker') === openKey) return;
    }
    // ⚠️ 必须记一笔: Flarum 的 focus trap (createFocusTrap, allowOutsideClick) 会在
    //    这次外部点击**之后**把焦点还给输入框, 于是 onfocus 紧接着又触发, 列表当场
    //    原样弹回来 —— 表现就是「怎么点都关不掉」。这个标记让紧随其后的那次 focus
    //    不再负责「打开」, 由 onFocusOpen 消费掉。
    this._state[openKey].suppressFocusOpen = true;
    this.closeDrop(openKey);
  }

  /**
   * v0.17.25 — 上膛期间**记录**用户落手的位置。
   *
   * ⚠️ 与 v0.17.13 的关键差异: **mousedown / touchstart 不再解除上膛**, 只把 `e.target`
   *   记进 `_userTouched`。原因见构造器注释 —— 宿主那次程序化 focus 是异步的
   *   (挂在 `.Modal` 的 transitionend 上), 完全可能排在用户第一次点击**之后**;
   *   v0.17.13 一被第一次点击解除上膛, 那次仍在途的程序化 focus 就失控了。
   *
   * ⚠️ 只认 `e.isTrusted` 为 true 的那一次 —— 用它确认「**这次输入真的是用户产生的**」。
   *   脚本派发的合成事件 (e.g. 自动化测试 `.dispatchEvent`) 不算数, 否则测试/程序化
   *   触发会把 `_userTouched` 填满, 把「免疫程序化 focus」这个开关自己废掉。
   *
   * keydown 仍然**直接解除上膛** —— Tab 的 trusted keydown 必然先于 focus 到达
   * (实测间隔 2.7ms), 这条 v0.17.13 的保证原样保留。
   *
   * ⚠️ 只记 mousedown / touchstart, **不**因为它们解除上膛; 也**不**监听 focusin
   *   来解除 —— 实测那样做会提前解除 (见下面 onOpeningFocus 被删掉的原因)。
   */
  onFirstUserInput(e) {
    if (!e.isTrusted) return;
    if (e.type === 'keydown') {
      this._releaseOpeningGuard();
      return;
    }
    if (!e.target) return;
    this._userTouched.add(e.target);
    // ⚠️ 必须把该 picker 的 **input** 也记上: 用户落手点未必是 <input> 本尊
    //   (`.ZS-agent-input-wrap` / 放大镜 <i> / 清除按钮 都会是 e.target),
    //   而 _isOpeningFocusTarget 判的是 focus 落点, 也就是 input。
    //   漏了这一步 → 用户点的那个框会被误判成「程序化 focus」而被吞掉。
    const box = e.target.closest && e.target.closest('.ZS-agent-search');
    if (box) {
      const inp = box.querySelector('input');
      if (inp) this._userTouched.add(inp);
    }
  }

  /**
   * v0.17.25 — 解除上膛的唯一出口: keydown / 程序化 focus 到达 / destroy。
   * 解除后这三个监听再无作用, 留着只是全站每次输入都跑一次空函数。
   */
  _releaseOpeningGuard() {
    this._awaitingFirstUserInput = false;
    window.removeEventListener('keydown', this._onFirstUserInput, true);
    window.removeEventListener('mousedown', this._onFirstUserInput, true);
    window.removeEventListener('touchstart', this._onFirstUserInput, true);
  }

  /**
   * v0.17.25 — 这个 focus 是不是「宿主 modal 那次程序化 focus」?
   *
   * 判据 = 落在本 modal 内 ∧ 用户**没有**在那个节点上落过手。
   * 「落过手」由 onFirstUserInput 记进 `_userTouched`。
   * ⚠️ 与时序**完全无关**: 用户先点后被抢焦点、先被抢焦点后自己点, 两种顺序都判得对。
   *   这正是 v0.17.13 那条「按先后顺序解除上膛」判据做不到的地方。
   */
  _isOpeningFocusTarget(t) {
    if (!this._awaitingFirstUserInput || !t) return false;
    const el = this.host && this.host.element;
    if (!el || !el.contains) return false;
    if (!el.contains(t)) return false;
    return !this._userTouched.has(t);
  }

  /**
   * v0.17.25 — 本文件**曾经**有过一个 `onOpeningFocus` (window 捕获 focusin,
   * 「落在 modal 内且用户没碰过 ⇒ 程序化 focus ⇒ 解除上膛」), 第一版修复里留着它当兜底。
   * ⚠️ **已删**, 实测它是提前解除上膛的元凶, 不是兜底:
   *   modal 一挂上, 在那次真正的程序化 focus (≈280ms) **之前**, 本 modal 内部早就发生过
   *   好几次 focusin (Flarum focus trap 的初始聚焦 / modal 容器自身等), 它们全都满足
   *   「在 modal 内 + 用户没碰过」⇒ 上膛在用户第一次点击**之前**就被解除,
   *   等程序化 focus 真正到达时已经没有闸了 —— 实测表现与不修时一模一样。
   *   教训: 「用户没碰过」不足以代表「这是那次程序化 focus」, 因为 focus 的来源不止一处。
   *   现在上膛只由 keydown / onFocusOpen 里那一次判定 / destroy 三条出口解除。
   */

  /**
   * focus 打开。带一道抑制闸, 见 onDocMousedown 里的说明。
   * 只抑制**一次**且仅在真的被外部点击关掉时, 所以键盘 Tab 进来仍然会展开。
   *
   * v0.17.13 新增第二道闸 `_awaitingFirstUserInput`; v0.17.25 把它的判据从
   * 「用户还没碰过 modal」改成「这次 focus 是不是那次程序化 focus」(见 `_isOpeningFocusTarget`)。
   * ⚠️ 顺序不可换: `suppressFocusOpen` (v0.16.6 ③) 必须留在**第一**位 ——
   *   它是「外部点击后 focus trap 把焦点还回来」的专用闸, 消费后必须复位,
   *   放第二位保证它的语义与 v0.16.6 逐字节一致。
   */
  onFocusOpen(key, e) {
    const s = this._state[key];
    // ── v0.19.01 — 移动端分流 (⛔ 桌面端那条路径一个字都不许动) ──
    if (this._ensurePhoneLayout()) {
      // ① v0.16.6 ③ 同款闸: 外部点击后 focus trap 把焦点还回来 ⇒ 这一次**不**开抽屉
      if (s.suppressFocusOpen) {
        s.suppressFocusOpen = false;
        return;
      }
      // ② v0.17.25 上膛: 宿主 modal 那次**程序化** focus ⇒ 不开抽屉 (并就此解除上膛)
      if (this._awaitingFirstUserInput && this._isOpeningFocusTarget(e && e.target)) {
        this._releaseOpeningGuard();
        return;
      }
      this.openSheet(key);
      return;
    }
    if (s.suppressFocusOpen) {
      s.suppressFocusOpen = false;
      return;
    }
    // v0.17.25 — 上膛期间: 用户在**这个框**上落过手 ⇒ 真 focus, 照常开;
    //   没落过手 ⇒ 宿主 modal 那次程序化 focus, 抑制开列表**并**就此解除上膛。
    if (this._awaitingFirstUserInput && this._isOpeningFocusTarget(e && e.target)) {
      this._releaseOpeningGuard();
      return;
    }
    if (s.open) return;
    s.open = true;
    this.redraw();
  }

  /**
   * Esc 关下拉, 同时**拦住它别把整个弹窗也带走**。
   *
   * ⚠️ Flarum 的 ModalManager 在 document.body 上挂了 keyup 监听
   *    (core ModalManager.tsx → handleEscPress), 那是整个弹窗的关闭入口,
   *    并且它**不** stopPropagation。所以只拦 keydown 是不够的 —— 同一个按键随后的
   *    keyup 照样会冒到 body 把弹窗关掉。因此 keydown / keyup 两个都拦:
   *      keydown → 关下拉 + preventDefault (顺带压掉浏览器的 CloseWatcher 关闭请求)
   *      keyup   → 只吞这一次、不重复关 (用 escKeyup 记账)
   *    下拉本来就关着时不记账, 用户下一次 Esc (想关弹窗) 仍正常生效。
   */
  onKeydown(key, e) {
    const s = this._state[key];
    // ── v0.19.01 — 移动端: Esc 关的是**抽屉**, ⛔ 不能把整个 modal 带走 (brief §4.7) ──
    if (this._ensurePhoneLayout()) {
      if (e.key !== 'Escape' || this._sheetKey !== key) return;
      e.preventDefault();
      e.stopPropagation();
      s.escKeyup = true; // keyup 也要拦 —— core ModalManager 挂在 body 上的 keyup 监听
      this.closeSheet(key); //   会把 modal 关掉 (只拦 keydown 不够, 见下面桌面端注释)
      return;
    }
    if (e.key !== 'Escape' || !s.open) return;
    e.preventDefault();
    e.stopPropagation();
    s.escKeyup = true;
    this.closeDrop(key);
  }
  onKeyup(key, e) {
    const s = this._state[key];
    if (e.key !== 'Escape' || !s.escKeyup) return;
    s.escKeyup = false;
    e.preventDefault();
    e.stopPropagation();
  }

  /**
   * 向上翻转防御 —— 祖先 `.Modal-content` 是 Flarum core 的 overflow:hidden
   * (不是我们的, 不劫持), 浮动下拉超出就会被**看不见地**裁掉。
   *
   * 只用 getBoundingClientRect() 比一下, 不引 Popper。
   * ⚠️ 比的是「假设向下展开时」的落点, 而不是直接读 drop 自己的 bottom ——
   *    一旦翻上去, drop 的 rect 已经在上方了, 拿它跟容器比会得出「没超出」的
   *    相反结论, 于是每轮 redraw 都在两个状态之间来回抖。
   *    drop 在流外, 不会反过来顶动锚点, 所以锚点 rect 永远稳定。
   */
  updateFlips() {
    const el = this.host && this.host.element;
    if (!el || !el.querySelector) return;
    const keys = Object.keys(this._state);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (!this._state[key].open) continue;
      // 只认自己这个 modal 的容器, 不用全局 .Modal-content (可能叠着别的 modal)
      const box = el.querySelector('.Modal-content');
      const drop = el.querySelector('.ZS-agent-drop[data-zs-picker="' + key + '"]');
      const anchor = el.querySelector('.ZS-agent-search[data-zs-picker="' + key + '"]');
      if (!drop || !anchor || !box) continue;
      const cRect = box.getBoundingClientRect();
      const aRect = anchor.getBoundingClientRect();
      const h = drop.getBoundingClientRect().height;
      const wouldBottom = aRect.bottom + 6 + h; // 向下时列表底边落点
      const wouldTop = aRect.top - 6 - h; // 向上时列表顶边落点

      const overflowBelow = wouldBottom > cRect.bottom;
      const fitsAbove = wouldTop >= cRect.top;
      // 上下都不够 → overflowBelow && !fitsAbove → 维持向下, 不做复杂降级。
      drop.classList.toggle('ZS-agent-drop--up', overflowBelow && fitsAbove);
    }
  }

  // ── 渲染 ────────────────────────────────────────────────────────

  /** 渲染一个下拉 (含 .ZS-form-group 外壳), 嵌进宿主模板即可 */
  render(key) {
    const cfg = this.fields[key];
    if (!cfg) {
      console.error('[searchable-user-picker] render 未注册的字段', key);
      return null;
    }
    const s = this._state[key];
    const selected = this.selectedUser(key);
    const list = this.filtered(key);

    // v0.17.12 — 先把 .ZS-agent-search 这一块单独摘出来。
    //   bare 模式 (宿主自己已有 <label> 和 .ZS-form-field 外壳) 直接返回它, 不套壳 —
    //   否则会出现两个标签, 并且破坏 .ZS-BookingModal .ZS-form-row-2col > .ZS-form-field
    //   这条桌面 grid item 选择器 与 @media 里的 .ZS-nf-* 钉位。
    const box = m("div", {
      className: "ZS-agent-search",
      "data-zs-picker": key,
      onkeydown: e => this.onKeydown(key, e),
      onkeyup: e => this.onKeyup(key, e)
    }, m("div", {
      className: "ZS-agent-input-wrap"
    }, m("span", {
      className: "ic"
    }, m("i", {
      className: "fas fa-magnifying-glass"
    })), m("input", {
      className: "ZS-form-input",
      placeholder: selected ? this.userName(selected) : cfg.placeholder || '输入姓名筛选…',
      value: s.query
      // v0.19.01 — 移动端: 这个输入框只是**入口**, 真正的搜索框在抽屉底部。
      //   ⛔ readonly ⇒ iOS **不会**因为它被聚焦就弹键盘 (否则抽屉一打开就被键盘顶掉半屏,
      //     辉哥 10/26 11:46 拍板「抽屉打开时不自动弹键盘」)。
      //   ⛔ 桌面端传 false ⇒ 属性不出现, 行为逐字节不变。
      ,
      readOnly: this._ensurePhoneLayout(),
      onfocus: e => this.onFocusOpen(key, e),
      onclick: () => {
        // v0.19.01 — 移动端: 点输入框 = **开抽屉** (brief §4.2)。
        //   ① focus 那一次通常已经开过了 (onFocusOpen);
        //   ② 但「外部点击后 focus trap 把焦点还回来」那一次会被 suppressFocusOpen 吞掉
        //      ⇒ 这时靠 onclick 补开 (与桌面端 v0.16.6 ④ 同一个道理)。
        if (this._ensurePhoneLayout()) {
          if (this._sheetKey !== key) this.openSheet(key);
          return;
        }
        // v0.16.6 — 候选项上的 onmousedown preventDefault 压掉了默认失焦,
        // 选完之后**焦点还留在输入框上**, 于是再点它不会触发 onfocus,
        // 列表就再也打不开了 (只打字能重开)。onclick 不管焦点在哪都触发, 补上这条。
        // 点在输入框外走 onDocMousedown 关闭, 所以这里只管「打开」。
        if (!s.open) {
          s.open = true;
          this.redraw();
        }
      },
      onblur: e => {
        // v0.19.01 — 移动端: 焦点移到抽屉里的搜索框 ⇒ modal 里这个输入框会 blur
        //   ⇒ ⛔ **不许**因此关掉抽屉 (brief §4.3)。桌面端那条「一律关」一字不动。
        if (this._ensurePhoneLayout()) return;
        // v0.16.6 — 失焦关闭。能选中是因为候选项上的 onmousedown
        //  preventDefault 压掉了这次失焦, click 才轮得到 choose。
        // v0.17.25 — ⚠️ 唯一例外: 上膛期间, 焦点是被宿主 modal 那次**程序化**
        //   focus 抢到本 modal 内另一个用户没碰过的控件上 (relatedTarget 判据,
        //   见 _isOpeningFocusTarget)。那不是用户自己移开的焦点, 用户刚点开的
        //   列表不该被它顶掉 —— 不关。程序化 focus 到达后 `onFocusOpen` 会解除
        //   上膛, 之后所有失焦恢复 v0.16.6 的「一律关」。
        if (!this._isOpeningFocusTarget(e && e.relatedTarget)) {
          this.closeDrop(key);
        }
      },
      oninput: e => {
        s.query = e.target.value;
        // v0.19.01 — 移动端: ⛔ **不许**再设 `s.open = true` (否则小浮层也冒出来,
        //   brief §4.4)。移动端这个框是 readonly, 正常根本不会走到这里。
        if (this._ensurePhoneLayout()) {
          this.redraw();
          return;
        }
        s.open = true;
        this.redraw();
      }
    }), selected && cfg.clearable !== false && m("button", {
      className: "ZS-agent-clear",
      type: "button",
      onclick: () => this.clear(key)
    }, m("i", {
      className: "fas fa-xmark"
    }))), s.open && m("div", {
      className: "ZS-agent-drop",
      "data-zs-picker": key
    }, list.length === 0 && m("div", {
      className: "ZS-agent-opt empty"
    }, cfg.emptyText || '当前校区下没有匹配的候选人'), list.slice(0, this.maxVisible).map(u => {
      const name = this.userName(u);
      const on = +this.idOfItem(key, u) === +this.getValue(key);
      return m("div", {
        className: 'ZS-agent-opt' + (on ? ' on' : ''),
        key: this.idOfItem(key, u)
        // v0.16.6 — ⚠️ 最关键的一行。浏览器按下鼠标的默认动作是
        //  把焦点挪走, blur 必然先于 click 触发; 不拦住的话
        //  下拉在 mousedown 那一刻就没了, 一条候选都选不中。
        ,
        onmousedown: e => e.preventDefault(),
        onclick: () => this.choose(key, u)
      }, m(Avatar, {
        user: this.avatarUser(u, name)
      }), m("span", {
        className: "nm"
      }, name), m("span", {
        className: 'rl' + (u.role === 'coach' ? ' coach' : '')
      }, u.role === 'coach' ? '教练' : u.role === 'sales' ? '销售' : u.role === 'manager' ? '店长' : u.role));
    })), this._sheetKey === key && this.renderSheet(key));

    // v0.17.12 — bare: 只输出 .ZS-agent-search。不传 bare 的老调用方走下面这条, DOM 一字不变。
    if (cfg.bare) return box;
    return m("div", {
      className: 'ZS-form-group ZS-userpicker-host' + (cfg.groupClass ? ' ' + cfg.groupClass : '')
    }, m("div", {
      className: "ZS-form-label"
    }, m("i", {
      className: 'fas ' + (cfg.icon || 'fa-user'),
      style: "color:var(--zs-text-2-8d96a3, #8d96a3);"
    }), " ", cfg.label, cfg.required ? m("span", {
      className: "req"
    }, "*") : null, cfg.hint ? m("span", {
      className: "hint"
    }, cfg.hint) : null, m("span", {
      className: "badge-search"
    }, "\u53EF\u641C\u7D22")), box, cfg.error ? m("div", {
      className: "ZS-phone-hint duplicate"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), " ", cfg.error) : null);
  }

  /**
   * v0.19.01 — 移动端**底部抽屉** (brief §3.1~§3.4 + 设计稿 design/mobile-search-picker/)。
   *
   * ## 形制 (照抄 core `Dropdown.less` 的 `@media @phone`, 只改一处)
   *   · `position: fixed` + `left/right: 0` + `bottom: 0` ⇒ 包含块 = 视口 (core 手机端
   *     `.Modal` 是 `transform: none !important` ⇒ fixed 不会被它接管, 也不会被
   *     `.Modal-content` 的 `overflow: hidden` 裁掉 —— 见文件头 §2.1)
   *   · `max-height: 70vh`; 键盘弹起时由 JS 给动态 bottom + 收缩后的 max-height (§4.5)
   *   · ⚠️ **有意偏离** core 的一处: 顶部 `12px` 圆角 (core 手机端是直角 0) ——
   *     更像抽屉、跟站内其它浮层一致 (辉哥 10/26 11:46 拍板「加」)
   *   · 阴影方向取**上方** (`0 -2px 6px`): 抽屉是从下往上来的 ⇒ 阴影在上方 (设计稿)
   *
   * ## ⛔ 零副作用
   *   · 候选**零新增网络请求** —— 用的是现有的 `st.list` + `filtered(key)` + `maxVisible`
   *   · 候选的数据源 / 字段 / 排序 / 条数上限**全部沿用现有实现**, 一个字没改
   *   · 图标一律 **Font Awesome** (⛔ 不用 emoji): fa-xmark / fa-check / fa-circle-xmark /
   *     fa-magnifying-glass
   */
  renderSheet(key) {
    const cfg = this.fields[key] || {};
    const s = this._state[key];
    const list = this.filtered(key);
    const total = (s.list || []).length;
    const vp = this._sheetViewport;
    const style = {};
    // 键盘弹起时的动态值 (brief §4.5); 平时留空 ⇒ CSS 的 `bottom:0` / `max-height:70vh` 生效
    if (vp.bottom) style.bottom = vp.bottom + 'px';
    if (vp.maxHeight) style.maxHeight = vp.maxHeight + 'px';
    return m("div", {
      className: "ZS-agent-sheet-layer",
      "data-zs-picker": key
    }, m("div", {
      className: "ZS-agent-sheet-backdrop",
      onclick: () => this.closeSheet(key)
    }), m("div", {
      className: "ZS-agent-sheet",
      style: style
    }, m("div", {
      className: "ZS-agent-sheet-head"
    }, m("div", {
      className: "ZS-agent-sheet-headtext"
    }, m("div", {
      className: "ZS-agent-sheet-title"
    }, this.sheetTitle(key)), m("div", {
      className: "ZS-agent-sheet-sub"
    }, "\u672C\u6821\u533A\u5171 ", total, " \u4F4D\u5019\u9009\u4EBA")), m("button", {
      className: "ZS-agent-sheet-close",
      type: "button",
      "aria-label": "\u5173\u95ED",
      onclick: () => this.closeSheet(key)
    }, m("i", {
      className: "fas fa-xmark"
    }))), m("div", {
      className: "ZS-agent-sheet-list"
    }, list.length === 0 && m("div", {
      className: "ZS-agent-sheet-opt empty"
    }, cfg.emptyText || '当前校区下没有匹配的候选人'), list.slice(0, this.maxVisible).map(u => {
      const name = this.userName(u);
      const on = +this.idOfItem(key, u) === +this.getValue(key);
      return m("div", {
        className: 'ZS-agent-sheet-opt' + (on ? ' on' : ''),
        key: this.idOfItem(key, u)
        // v0.16.6 同款: 压掉 mousedown 的默认失焦 ⇒ click 才轮得到 choose。
        //   移动端还顺带保住了「键盘别收」—— 焦点留在抽屉底部的搜索框上。
        ,
        onmousedown: e => e.preventDefault(),
        onclick: () => this.choose(key, u)
      }, m(Avatar, {
        user: this.avatarUser(u, name)
      }), m("span", {
        className: "nm"
      }, this.highlightName(name, s.query)), m("span", {
        className: 'rl' + (u.role === 'coach' ? ' coach' : '')
      }, this.roleLabel(u)), on && m("i", {
        className: "fas fa-check ZS-agent-sheet-tick"
      }));
    })), m("div", {
      className: "ZS-agent-sheet-foot"
    }, m("div", {
      className: "sw"
    }, m("span", {
      className: "ic"
    }, m("i", {
      className: "fas fa-magnifying-glass"
    })), m("input", {
      className: "ZS-agent-sheet-input",
      type: "text",
      placeholder: cfg.placeholder || '输入姓名筛选…',
      value: s.query
      // ⛔ 只改查询词 + 重画 ⇒ 走**现有**的 filtered(key) 过滤, 零新增请求 (brief §4.4)
      ,
      oninput: e => {
        s.query = e.target.value;
        this.redraw();
      }
    }), s.query ? m("button", {
      className: "ZS-agent-sheet-clear",
      type: "button",
      "aria-label": "\u6E05\u9664"
      // 同款 preventDefault: 别把焦点从搜索框上带走 (键盘别收)
      ,
      onmousedown: e => e.preventDefault(),
      onclick: () => {
        s.query = '';
        this.redraw();
      }
    }, m("i", {
      className: "fas fa-circle-xmark"
    })) : null))));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/SearchableUserPicker', SearchableUserPicker);